import { Checkbox, FormItem, ValidationError } from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { isNotNilOrEmpty } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { useUnlinkAnotherCredentialsConfirmationDialog } from "./UnlinkAnotherCredentialsConfirmationDialog";

export type LinkFormItemProps = {
	attachable: IValueInputAttachable<boolean>;
	canBeEnabled: boolean;
	text: string;
	configuredByOtherCredentialName: string | null;
};

export const LinkFormItem = (props: LinkFormItemProps): ReactElement => {
	const {
		attachable,
		canBeEnabled,
		text,
		configuredByOtherCredentialName
	} = props;

	const label: string = createLinkLabel(text, configuredByOtherCredentialName);

	const [unlinkAnotherConfirmationDialog, openUnlinkAnotherConfirmationDialog] = useUnlinkAnotherCredentialsConfirmationDialog();

	const changeHandler = useCallback(
		async (value: boolean): Promise<void> => {
			const applyValue = (): void => {
				attachable.reducer
					.setValue(value);
				attachable.validation
					.triggerInputFinished();
			};

			if (!value || !configuredByOtherCredentialName) {
				applyValue();
				return;
			}

			const result: TryResult<void> = await openUnlinkAnotherConfirmationDialog({
				credentialTypeName: text,
				configuredByOtherCredentialName
			});

			if (result.wasSuccessful) {
				applyValue();
			}
		},
		[
			configuredByOtherCredentialName,
			attachable,
			openUnlinkAnotherConfirmationDialog,
			text
		]
	);

	const isEnabled: boolean = canBeEnabled || attachable.value;

	return (
		<FormItem>
			<Checkbox
				{...attachable.attachToCheckbox()}
				onChange={changeHandler}
				isDisabled={!isEnabled}
				text={label}
			/>
			<ValidationError {...attachable.attachToValidationError()} />
			{unlinkAnotherConfirmationDialog}
		</FormItem>
	);
};
LinkFormItem.displayName = "LinkFormItem";

export function createLinkLabel(text: string, configuredByOtherCredentialName: string | nil): string {
	return isNotNilOrEmpty(configuredByOtherCredentialName)
		? `${text} (${localize("_UsedBy").toLowerCase()}: ${configuredByOtherCredentialName})`
		: text;
}